// React & Hooks
import { useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers
import { useTranslate } from "helpers";

// Navigacija
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// API
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { useApiCall } from "services/api/hooks/useApiCall";

// API Tipovi & Konstante
import { FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";

// UI Komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Button } from "ui/components/Button/Button";

// Lokalne komponente
import VehicleDetailCard from "./components/VehicleDetailCard/VehicleDetailCard";
import { VehicleDetailRef } from "./components/VehicleDetailCard/types";
import TabMenu from "../components/TabMenu/TabMenu";

// Tipovi
import { VehicleDetailPageCmp, VehicleDetailPageProps } from "./types";

import { SIDEBAR_ITEM_FLEET_VEHICLE } from "app/components/Sidebar/consts";
import { FLEET_VEHICLE_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import VehicleActions from "./components/VehicleActions/VehicleActions";
import { VehicleStatusEditCard } from "../components/VehicleStatusEditCard";
import { Loader } from "ui/components/Loader";


const VehicleDetailPage: VehicleDetailPageCmp = (
  props: VehicleDetailPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [userWidgets, setUserWidgets] = useState<string[]>([]);

  const vehicleDetailRef = useRef<VehicleDetailRef>(null);

  // Dohvati vozilo po ID-u, ako vrati 404 redirecta na search
  const { data: formValues, isLoading, refetch } = useApiCall(
    () => getFleetVehicleAPI(id!),
    [id],
    { onNotFound: FleetVehicleRoutes.FleetVehicleSearchPageRouter }
  );

  // Promjena statusa mijenja i dostupne akcije (prodano vozilo je arhivirano),
  // pa se vozilo ponovno dohvaća umjesto da se status drži samo lokalno.
  const handleStatusChange = useCallback(() => {
    refetch();
  }, [refetch]);

  const title = FLEET_VEHICLE_DETAIL_PAGE_TITLE;

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_FLEET_VEHICLE.text,
      route: SIDEBAR_ITEM_FLEET_VEHICLE.route,
    },
  ];

  if (!id) return <></>;

  if (!formValues) return <Loader isLoading={isLoading}>{null}</Loader>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  {formValues.defaultImage && (
                    <img src={formValues.defaultImage} alt={formValues.name ?? ""} title={formValues.name ?? ""} className="h-12 w-12 object-contain rounded-full" />
                  )}
                  <div>
                    <div className="flex gap-2">
                      <PageTitle
                        title={`${t(title.text)} ${formValues.name ? `[${formValues.name}]` : ""}`}
                        icon={title.icon}
                        iconColor={title.iconColor}
                      />
                      <div>
                        <VehicleStatusEditCard
                          vehicleId={id}
                          currentStatus={
                            (formValues.status as FleetVehicleStatusEnum) ??
                            FleetVehicleStatusEnum.AVAILABLE
                          }
                          onStatusChange={handleStatusChange}
                        />
                      </div>
                    </div>
                    <Breadcrumb items={breadcrumbs} />
                  </div>
                </div>

              </div>
              <div className="flex gap-2 items-center">
                <VehicleActions
                  vehicleId={id}
                  isSold={formValues.status === FleetVehicleStatusEnum.SOLD}
                  vehicleDetailRef={vehicleDetailRef}
                  userWidgets={userWidgets}
                />
                <Button
                  variant="secondary"
                  startIcon="/media/icons/setting.svg"
                  onClick={() => setShowAddWidgetModal(true)}

                ></Button>

              </div>
            </div>
          </PageHeader>
          <TabMenu vehicleId={id} />
        </div>
        <div className="px-0 py-4 mx-[-1px] xl:pl-4">
          <VehicleDetailCard
            ref={vehicleDetailRef}
            fleetVehicleDetail={formValues}
            showAddWidgetModal={showAddWidgetModal}
            setShowAddWidgetModal={setShowAddWidgetModal}
            onVehicleRefresh={refetch}
            onUserWidgetsChange={setUserWidgets}
          />
        </div>
      </div>
    </>
  );
};

VehicleDetailPage.route = FleetVehicleRoutes.FleetVehicleDetailPageRouter;

export default VehicleDetailPage;
